import {Component, EventEmitter, Input, Output, ViewChild, ChangeDetectorRef, Inject} from '@angular/core';
import {DomSanitizer, SafeHtml} from '@angular/platform-browser'

import {ConfigService} from '../config/config.service';
import {CsvConverterService} from '../converter/csv-converter.service';
import {DownloadService} from '../converter/download.service';
import {HmlValidationService} from '../validation/hml-validation.service';
import {HmlSubmissionService} from '../submission/hml-submission.service';

import {AppComponent} from '../app.component';
import {ModalComponent} from '../modal/modal.component';
import {FileRecordComponent} from '../records/file-record.component';
import {ValidationErrorsComponent} from './validation-errors.component';

import {FileRecordObject} from '../records/file-record';
import {ModalConfig} from '../modal/modal-config';

import {HttpResponse} from '@angular/common/http'

import {FileRecordService} from "../records/file-record.service";
import {AuthService} from "../auth/services/auth-service";
import {AuthenticatedResult} from "angular-auth-oidc-client";
import {Observable} from "rxjs";
import {NgbCollapse} from "@ng-bootstrap/ng-bootstrap";

@Component({
  selector: 'data-table-component',
  templateUrl: './data-table.component.html',
  styleUrls: ['./data-table.component.css'],
  providers: [CsvConverterService, HmlValidationService, HmlSubmissionService, FileRecordService, DownloadService, ConfigService]
})
export class DataTableComponent {

  submissionData: String;
  previousSectionCollapsed: boolean = true;

  @ViewChild('hmlPreview')
  private modalHmlPreview: ModalComponent;
  @ViewChild('fileDownload')
  private modalFileDownload: ModalComponent;
  @ViewChild('validateOrSubmit')
  private modalValidateOrSubmit: ModalComponent;
  @ViewChild('rejectConfirmation')
  private modalRejectConfirmation: ModalComponent;
  @ViewChild('transmissionError')
  private modalTransmissionError: ModalComponent;
  @ViewChild(ValidationErrorsComponent)
  private modalValidationErrors: ValidationErrorsComponent;

  @Output() updateTable: EventEmitter<any> = new EventEmitter();

  public modalTitle: string;
  public modalTitle2: string;
  public hmlFileId: string;
  public downloadSizeMsg: string;
  public modalContent: SafeHtml;
  public modalConfig: ModalConfig;
  @Input() activeFileRecordList: FileRecordObject[];
  @Input() previousFileRecordList: FileRecordObject[];

  isAuthenticated$: Observable<AuthenticatedResult>;

  // The activeChild field is necessary to keep a reference to the file record
  // that was clicked while the validateOrSubmit modal gets popped up
  public activeChild: FileRecordComponent;

  constructor(private csvConverterService: CsvConverterService,
              private downloadService: DownloadService,
              private sanitizer: DomSanitizer,
              private hmlValidationService: HmlValidationService,
              private hmlSubmissionService: HmlSubmissionService,
              private readonly fileRecordService: FileRecordService,
              private ref: ChangeDetectorRef,
              public appComponent: AppComponent,
              private configService: ConfigService,
              private readonly authService: AuthService) {
    this.isAuthenticated$ = this.authService.isAuthenticated();
  }

  ngOnInit(): any {
    this.styleScrollbar("active-section");
  }

  public styleScrollbar(sectionId: string): void {
    let selector = "#" + sectionId + " .session-table";
  }


  // These are the text values of the <th> column header cells in the table
  get headers(): string[] {
    return FileRecordComponent.headers();
  }

  // Relative widths (percentage) of the table columns.
  // i.e. <col style="width: 23%;">
  get columnWidths(): string[] {
    return FileRecordComponent
      .columnWidths()
      .map((percent) => {
        return percent + "%";
      });
  }

  public toggleCollapse(): void {
    if (!this.previousSectionCollapsed) {
      document.querySelector("#toggle-button").textContent = "Show Previous Sessions";
      this.previousSectionCollapsed = true;
    } else {
      document.querySelector("#toggle-button").textContent = "Hide Previous Sessions";
      this.previousSectionCollapsed = false;
      this.styleScrollbar("previous-section");
    }
  }

  public doConvertCsv(component: FileRecordComponent): void {
    component.actionPending = true;
    this.csvConverterService.convertCsvFile(component.fileRecord.id)
      .then((response: HttpResponse<string>) => {
        if (response && response.body.length > 0) {
          // set the modal title
          const hmlFileName = response.headers.get("X-NMDP-FILE-NAME");
          this.modalTitle = `Preview File ${hmlFileName}`;
          // set the modal content
          const htmlText = this.toHtmlText(response.body);
          const safeHtml = this.sanitizer.bypassSecurityTrustHtml(htmlText);
          this.modalContent = safeHtml;
          // set the content of the file download button
          const contentLength = response.headers.get("Content-Length");
          this.downloadSizeMsg = this.sizeString(Number(contentLength));
          const cbTitle = `<span class='glyphicon glyphicon-download-alt'></span>Download File (${this.downloadSizeMsg})`;
          this.modalFileDownload.cancelButtonTitle(cbTitle);
          // download button will need the file iid
          this.hmlFileId = response.headers.get("X-NMDP-FILE-IID");
          this.modalHmlPreview.open();
        }
        this.updateTable.emit(null);
      });
  }

  public validateOrSubmitHml(component: FileRecordComponent): void {
    this.activeChild = component;
    this.isAuthenticated$.subscribe((authResult) => {
      if (!authResult.isAuthenticated) {
        this.doValidateHml(null);
      } else {
        this.modalTitle = `Validate or Submit HML File`;
        this.modalValidateOrSubmit.open();
      }
    })
  }

  public doValidateHml(e: any): void {
    this.activeChild.actionPending = true;
    this.hmlValidationService.validateHmlFile(this.activeChild.fileRecord.id)
      .then(validationResponse => {
        if (validationResponse) {
          this.modalValidateOrSubmit.close();
        }
        this.updateTable.emit(null);
      }).catch(error => {
      this.handleError(error);
    });
  }

  public doSubmitHml(component: FileRecordComponent): void {
    if (component !== null) {
      // If the component argument is null, that means we got here via the
      // validateOrSubmit modal.  activeChild should already be set in that
      // case.
      // If the component argument is not null, that means this function was
      // called in response to "Submit" being clicked on the table record.
      // In that case, activeChild is null and needs to be set to the value
      // that was passed.
      this.activeChild = component;
    }
    this.isAuthenticated$.subscribe((authResult) => {
      if (authResult.isAuthenticated) {
        this.activeChild.actionPending = true;
        this.hmlSubmissionService.submitHmlFile(this.activeChild.fileRecord.id)
          .then((data) => {
            this.submissionData = data as string;
            this.updateTable.emit()
          })
          .catch(this.handleError)
      }
    });
  }

  private handleError(error: any) {
    this.activeChild.actionPending = false;
    this.modalTitle = `Error`;
    this.modalContent = error.statusText;
    this.modalTransmissionError.open();
    this.updateTable.emit(null);
  }

  // For a given file size (in bytes), returns a simplified size string to
  // append to the download button.
  // Example: sizeString(2345) -> "2 kB"
  private sizeString(numBytes: number): string {
    let unit = numBytes;
    if (numBytes < 1000) {
      return Math.floor(unit) + " B";
    }
    if (numBytes < 1000000) {
      unit /= 1000;
      return Math.floor(unit) + " kB";
    }
    if (numBytes < 1000000000) {
      unit /= 1000000;
      return Math.floor(unit) + " MB";
    }
    unit /= 1000000000;
    return Math.floor(unit) + " GB";
  }

  // Converts the given xml text to html consisting of a '\n'-separated
  // sequence of <div> elements
  private toHtmlText(xmlText: string): string {
    return "<div class='hml-xml'>" + this.toHtmlDivs(xmlText) + "</div>";
  }

  private toHtmlDivs(xmlText: string): string {
    const lines = xmlText.split("\n");
    const result: string[] = new Array(lines.length);
    // The last line of xmlText is a blank.
    for (let i = 0; i < lines.length - 1; i++) {
      let line: string = lines[i];
      line = this.toHtmlDiv(line);
      result.push(line);
    }
    return result.join("");
  }

  // Converts one HML line to a <div> element
  private toHtmlDiv(line: string): string {
    if (line == "") {
      // Browsers squash empty lines on the page, so converting each empty
      // line to a single space preserves visual formatting when displayed
      line = " ";
    }
    // The return type is HTML, so tag characters need to be converted
    line = line.replace(/</g, "&lt;");
    line = line.replace(/>/g, "&gt;");

    // Converting all spaces to &nbsp; preserves whitespace formatting
    line = line.replace(/ /g, "&nbsp;");

    // Search for <sample> elements in the HML and wrap the 'id' attribute in
    // a <span> that can be styled (via css class 'hml-sample-hilite').
    line = line.replace
    (/&lt;sample(.*)id="([^"]*)"/g,
      '&lt;sample$1<span class="hml-sample-hilite">id="$2"</span>');

    // Search for <glstring> elements in the HML and wrap the element's
    // content in a <span> that can be styled
    // (via css class 'hml-glstring-hilite').
    line = line.replace
    (/&lt;glstring&gt;(.*)&lt;\/glstring&gt;/g,
      '&lt;glstring&gt;<span class="hml-glstring-hilite">$1</span>&lt;/glstring&gt;');

    // Search for <haploid> elements in the HML and wrap the 'type' attribute
    // in a <span> that can be styled (via css class 'hml-haploid-hilite').
    line = line.replace
    (/&lt;haploid(.*)type="([^"]*)"/g,
      '&lt;haploid$1<span class="hml-haploid-hilite">type="$2"</span>');

    return "<div>" + line + "</div>\n";
  }

  public hmlPreviewAccepted(event: any): void {
    this.modalFileDownload.open();
    this.updateTable.emit(null);
  }

  public hmlPreviewRejected(event: any): void {
    this.modalTitle2 = 'Reject File';
    this.modalRejectConfirmation.open();
  }

  public hmlRejectConfirmation(event: any): void {
    this.csvConverterService.rejectConvertedCsvFile(this.hmlFileId)
      .then(rejectResponse => {
        if (rejectResponse) {
          this.modalHmlPreview.closeNow();
        }
        this.updateTable.emit(null);
      });

  }

  public hmlRejectionCancellation(event: any): void {

  }

  public closeAndContinue(event: any): void {
    this.updateTable.emit(null);
  }

  public downloadFile(event: any): void {
    this.downloadService.downloadFile(this.hmlFileId)
      .then(fileResponse => {
        const blob = new Blob([fileResponse.body.toString()], {type: 'application/xml'});
        let link = document.createElement('a');
        link.href = window.URL.createObjectURL(blob);
        link.download = "converted.xml";
        link.click();
      })
  }

  public showValidationErrors(fileRecord: FileRecordObject): void {
    if (!fileRecord.errors || fileRecord.errors.length < 1) {
      this.fileRecordService.getHistoryTableErrorContent(fileRecord.id)
        .then(errors => {
          if (errors) {
            fileRecord.errors = errors;
            this.modalValidationErrors.open(fileRecord);
          }
        }).catch(error => {
        this.handleError(error);
      });
    } else {
      this.modalValidationErrors.open(fileRecord);
    }
  }

  public viewSubmittedFile(uuid: String): void {
    window.open(this.configService.dashboardUI + "/submissions/submission/" + uuid, "_blank");
  }
}
